'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface Tab {
  id: string;
  name: string;
}

interface TabsContextType {
  tabs: Tab[] | null;
  activeTab: string | null;
  onTabChange: ((tabId: string) => void) | null;
  setTabsConfig: (
    tabs: Tab[] | null,
    activeTab: string | null,
    onChange: ((tabId: string) => void) | null
  ) => void;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

export function TabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<Tab[] | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [onTabChange, setOnTabChange] = useState<((tabId: string) => void) | null>(null);

  const setTabsConfig = useCallback(
    (
      newTabs: Tab[] | null,
      newActiveTab: string | null,
      onChange: ((tabId: string) => void) | null
    ) => {
      setTabs(newTabs);
      setActiveTab(newActiveTab);
      setOnTabChange(() => onChange);
    },
    [] // Empty dependency array - this function should never change
  );

  return (
    <TabsContext.Provider value={{ tabs, activeTab, onTabChange, setTabsConfig }}>
      {children}
    </TabsContext.Provider>
  );
}

export function useTabs() {
  const context = useContext(TabsContext);
  if (context === undefined) {
    throw new Error('useTabs must be used within a TabsProvider');
  }
  return context;
}
